import * as React from "react";

import {HEADER_LOCALES} from "/src/data/header";
import {css} from "/src/utils/utils";
import {generateKeyId} from "/src/utils/utils";
import {NetlifyCountry, handleLangChange} from "/src/utils/header-nav";
import MobileNavigationSelector from "../mobile-navigation-selector/MobileNavigationSelector";
import {LOCALE} from "/src/config";

import * as styles from "./mobile-language-selector.module.scss";

interface MobileLanguageSelectorProps {
  disabled: boolean;
  onOpen: () => void;
  onClose: () => void;
}

const MobileLanguageSelector = (
  props: MobileLanguageSelectorProps
): React.ReactNode => {
  const currentLocaleValue = (LOCALE || "en-US").split("-")[1];
  const defaultLocaleIndex = HEADER_LOCALES.items.findIndex(
    item => item.country === currentLocaleValue
  );

  const [selectedOptionIndex, setSelectedOptionIndex] =
    React.useState<number>(defaultLocaleIndex);
  const {disabled, onClose, onOpen} = props;

  const handleLangSelection = React.useCallback((index: number) => {
    setSelectedOptionIndex(index);
    handleLangChange(HEADER_LOCALES.items[index].country as NetlifyCountry);
  }, []);

  const createLangSelectionHandler = React.useCallback(
    (index: number) => () => handleLangSelection(index),
    [handleLangSelection]
  );

  return (
    <MobileNavigationSelector
      disabled={disabled}
      label={HEADER_LOCALES.items[selectedOptionIndex].label}
      numberOfItems={HEADER_LOCALES.items.length}
      onOpen={onOpen}
      onClose={onClose}
      onSelect={handleLangSelection}
    >
      {(focusedListItem: number) =>
        HEADER_LOCALES.items.map(({label, country}, index: number) => {
          const isSelected = selectedOptionIndex === index;
          const hasFocus = focusedListItem === index;
          /* keyboard event is handled on parent level */
          return (
            <li
              key={generateKeyId(`${country}-${label}`)}
              className={css(
                styles.mobileLanguageSelector__item,
                isSelected ? styles.mobileLanguageSelector__itemSelected : "",
                hasFocus ? styles.mobileLanguageSelector__itemFocus : ""
              )}
              aria-selected={isSelected}
              role="option"
              aria-label={label}
              onClick={createLangSelectionHandler(index)}
            >
              {label}
            </li>
          );
        })
      }
    </MobileNavigationSelector>
  );
};

export default MobileLanguageSelector;
